import { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { uniqueChannelName } from '../../lib/realtime';
import { supabase } from '../../lib/supabase';
import { Switch } from '../ui';

type Gate = 'spec' | 'build' | 'approve';
type Row = Record<Gate, boolean>;

/**
 * What each switch skips. The database does the skipping (public.queue_auto_approve and the ai_jobs status triggers),
 * so it holds overnight with no tab open. None of them ever apply to Security Audit jobs.
 */
const GATES: { id: Gate; label: string; hint: string }[] = [
  {
    id: 'spec',
    label: 'Spec',
    hint: 'Specs approve themselves and go straight to build. Open decisions take the agent’s recommendation. Jobs stop at their deploy preview.',
  },
  {
    id: 'build',
    label: 'Build',
    hint: 'Finished builds skip your preview review: the approval agent merges and puts them live.',
  },
  {
    id: 'approve',
    label: 'Approve',
    hint: 'Finished jobs are marked submitted and leave the queue. The client note is not sent anywhere.',
  },
];

/** Global auto-approve switches, in the Agents sub-header next to the queue breaker. */
export default function AutoApproveToggles() {
  const { user } = useUser();
  const [row, setRow] = useState<Row | null>(null);
  const [busy, setBusy] = useState<Gate | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data, error: err } = await supabase
        .from('queue_auto_approve')
        .select('spec, build, approve')
        .eq('id', 1)
        .maybeSingle();
      if (err) throw err;
      if (!cancelled && data) setRow(data as Row);
    };
    load().catch((err) => {
      console.error('Error loading auto-approve:', err);
      if (!cancelled) setError('Auto-approve unavailable');
    });
    const channel = supabase
      .channel(uniqueChannelName('queue_auto_approve'))
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'queue_auto_approve' },
        () => {
          load().catch((e) =>
            console.error('Error refreshing auto-approve:', e),
          );
        },
      )
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  const flip = async (gate: Gate, next: boolean) => {
    if (!row || busy) return;
    setBusy(gate);
    setError(null);
    const prev = row;
    setRow({ ...row, [gate]: next });
    try {
      // Read back: an RLS-blocked update reports no error and changes nothing.
      const { data, error: err } = await supabase
        .from('queue_auto_approve')
        .update({ ...({ [gate]: next } as Partial<Row>), updated_by: user?.id ?? null })
        .eq('id', 1)
        .select('spec, build, approve')
        .maybeSingle();
      if (err) throw err;
      if (!data) throw new Error('not saved');
      setRow(data as Row);
    } catch (err) {
      console.error('Error updating auto-approve:', err);
      setRow(prev);
      setError(`Could not change ${gate}`);
    } finally {
      setBusy(null);
    }
  };

  const any = !!row && (row.spec || row.build || row.approve);
  // Where a job stops with these switches, in one line.
  const stops = !row
    ? ''
    : !row.spec && !row.build
      ? ''
      : row.spec && !row.build
        ? 'Jobs build on their own and stop at the deploy preview.'
        : row.build && !row.spec
          ? 'Specs still wait for you; finished builds go live without review.'
          : row.approve
            ? 'Jobs run all the way to live and leave the queue without review.'
            : 'Jobs run all the way to live without review; you see them at approval.';
  return (
    <div
      // Right-aligned beside the breaker on desktop; on a phone it starts the line like everything else.
      className='items-start md:items-end'
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        gap: '2px',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px 12px',
          whiteSpace: 'nowrap',
          flexWrap: 'wrap',
        }}
      >
        {error ? (
          <span
            style={{ fontSize: 'var(--text-xs)', color: 'var(--danger-400)' }}
          >
            {error}
          </span>
        ) : null}
        <span
          className='t-eyebrow'
          style={{ color: any ? 'var(--accent-text)' : 'var(--text-faint)' }}
          title='Skip the places a job waits for you. Applies to every job in the queue.'
        >
          Auto-approve
        </span>
        {GATES.map((g) => {
          const on = !!row?.[g.id];
          const live = g.id === 'build' && on;
          return (
            <span key={g.id} title={g.hint} style={{ display: 'inline-flex' }}>
              <Switch
                checked={on}
                disabled={!row || busy !== null}
                onChange={(next) => void flip(g.id, next)}
                label={
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight:
                        'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                      color: live
                        ? 'var(--danger-400)'
                        : on
                          ? 'var(--text-body)'
                          : 'var(--text-muted)',
                    }}
                  >
                    {g.label}
                    {live ? ' · goes live' : ''}
                  </span>
                }
              />
            </span>
          );
        })}
      </div>
      {stops ? (
        <span
          style={{
            fontSize: 'var(--text-xs)',
            color: row?.build ? 'var(--danger-400)' : 'var(--text-faint)',
          }}
        >
          {stops}
        </span>
      ) : null}
    </div>
  );
}
